<template>
    <x-form ref="clientCompanyForm" @submit.prevent="submit">
        <x-container fluid grid-list-lg>
            <x-layout>
                <x-flex xs12>
                    <h2>{{ edit ? "Edit" : "Add" }} Client</h2>
                </x-flex>
            </x-layout>

            <x-layout>
                <x-flex xs12>
                    <x-text-field
                        v-model="company.name"
                        label="Name"
                        placeholder=" "
                        required
                        :rules="[(v) => ruleRequired(v, 'Name')]"
                        outline
                        :disabled="isReadOnly"
                    />
                </x-flex>
            </x-layout>

            <x-layout>
                <x-flex xs12>
                    <x-text-field
                        v-model="company.address.line3"
                        label="Building number"
                        placeholder=" "
                        outline
                        :disabled="isReadOnly"
                    />
                </x-flex>
            </x-layout>

            <x-layout>
                <x-flex xs12>
                    <x-text-field
                        v-model="company.address.line1"
                        label="Address line 1"
                        placeholder=" "
                        required
                        :rules="[(v) => ruleRequired(v, 'Address line 1')]"
                        outline
                        :disabled="isReadOnly"
                    />
                </x-flex>
            </x-layout>

            <x-layout>
                <x-flex xs12>
                    <x-text-field
                        v-model="company.address.line2"
                        label="Address line 2"
                        placeholder=" "
                        outline
                        :disabled="isReadOnly"
                    />
                </x-flex>
            </x-layout>

            <x-layout>
                <x-flex xs12>
                    <x-text-field
                        v-model="company.address.city"
                        label="City/Town"
                        placeholder=" "
                        outline
                        :disabled="isReadOnly"
                    />
                </x-flex>
            </x-layout>

            <x-layout>
                <x-flex xs12>
                    <x-text-field
                        v-model="company.address.postcode"
                        label="Post code"
                        placeholder=" "
                        required
                        :rules="[(v) => ruleRequired(v, 'Post code')]"
                        outline
                        :disabled="isReadOnly"
                    />
                </x-flex>
            </x-layout>

            <x-layout>
                <x-flex xs12>
                    <x-select
                        v-model="company.address.country_id"
                        :items="countries"
                        item-text="name"
                        item-value="id"
                        label="Country"
                        :rules="[v => ruleRequired(v, 'Country')]"
                        outline
                        :disabled="isReadOnly"
                        attach
                    />
                </x-flex>
            </x-layout>

            <x-layout>
                <x-flex xs12>
                    <x-text-field
                        v-model="company.address.latitude"
                        label="Latitude"
                        placeholder=" "
                        outline
                        :rules="[
                            v => ruleLessThanOrEqualTo(v, 90),
                            v => ruleGreaterThanOrEqualTo(v, -90)
                        ]"
                        :disabled="isReadOnly"
                    />
                </x-flex>
            </x-layout>

            <x-layout>
                <x-flex xs12>
                    <x-text-field
                        v-model="company.address.longitude"
                        label="Longitude"
                        placeholder=" "
                        outline
                        :rules="[
                            v => ruleLessThanOrEqualTo(v, 180),
                            v => ruleGreaterThanOrEqualTo(v, -180)
                        ]"
                        :disabled="isReadOnly"
                    />
                </x-flex>
            </x-layout>

            <x-layout>
                <x-flex xs12>
                    <x-text-field
                        v-model="company.address.duns_ref"
                        label="DUNS Reference"
                        placeholder=" "
                        outline
                        :disabled="isReadOnly"
                    />
                </x-flex>
            </x-layout>

            <x-layout>
                <x-flex xs12>
                    <x-select
                        v-model="company.company_type"
                        :items="companyTypes"
                        item-text="name"
                        item-value="type"
                        label="Company type"
                        disabled
                        outline
                    />
                </x-flex>
            </x-layout>

            <x-layout>
                <x-flex xs12>
                    <x-select
                        v-model="status"
                        :items="statuses"
                        item-text="name"
                        item-value="type"
                        label="Status"
                        disabled
                        outline
                    />
                </x-flex>
            </x-layout>

            <x-layout>
                <x-flex>
                    <x-btn
                        class="ma-0"
                        color="tertiary"
                        @click="cancelClientCreation"
                    >
                        Cancel
                    </x-btn>
                </x-flex>

                <x-flex text-xs-right>
                    <x-btn
                        v-if="!isReadOnly"
                        class="ma-0"
                        color="primary"
                        type="submit"
                        :loading="loading"
                    >
                        Save
                    </x-btn>
                </x-flex>
            </x-layout>
        </x-container>

        <modal
            ref="messageDialogModal"
            :title=messageDialogTitle
            :confirmation="showModalConfirmation"
            :cancel-text="'No'"
            max-width="620"
        >
            <ul>
                <li v-for="message in this.messageDialogText">
                    {{ message }}
                </li>
            </ul>
        </modal>
    </x-form>
</template>

<script>
import rules from '~/mixins/rules';
import Modal from '~/components/Shared/UIComponents/Modal';

export default {
    name: 'ClientForm',
    components: {Modal},
    props: {
        clientCompany: {
            type: Object,
            default: () => {
                return {
                    address: {},
                    company_type: 'client'
                }
            }
        },
        edit: {
            type: Boolean,
            default: false
        }
    },
    mixins: [rules],
    data() {
        return {
            isReadOnly: false,
            company: this.defaultCompanyData(),
            messageDialogTitle: '',
            messageDialogText: [],
            loading: false,
            countries: this.$root.countries,
            companyTypes: [
                {
                    name: 'Client',
                    type: 'client'
                }
            ],
            status: 'active',
            statuses: [
                {
                    name: 'Active',
                    type: 'active'
                }
            ],
            showModalConfirmation: false
        };
    },
    mounted() {
        this.$eventBus.$emit('componentWillHandleErrorCodes', {
            componentID: this._uid,
            codesArray: [422]
        });

        this.company = {...(this.edit ? this.clientCompany : this.defaultCompanyData())};

        // prevent companies without address from breaking the form
        if (!this.company.address) {
            this.company.address = {};
        }
    },
    methods: {
        async cancelClientCreation() {
            if (!this.edit && !this.isReadOnly) {
                this.messageDialogText = ['Any unsaved data will be lost. Are you sure you want to cancel?'];
                this.showModalConfirmation = true;
                if (await this.$refs.messageDialogModal.open() === false) {
                    this.showModalConfirmation = false;
                    return;
                }
                this.showModalConfirmation = false;
            }
            this.$emit('close');
        },
        defaultCompanyData() {
            return {
                address: {},
                company_type: 'client'
            };
        },
        submit() {
            if (this.$refs.clientCompanyForm.validate()) {
                this.loading = true;
                const request = this.edit
                    ? axios.put(this.$root.api(`broker/client/${this.clientCompany.id}`), this.company)
                    : axios.post(this.$root.api('broker/client'), this.company);

                request.then(async () => {
                    this.$emit('update');

                    this.messageDialogTitle = 'Success';
                    this.messageDialogText = [`Client ${this.company.name} has been successfully saved`];
                    this.isReadOnly = true;
                    if (await this.$refs.messageDialogModal.open()) {
                        this.$emit("clientUpdated");
                        this.$emit('close');
                    }
                }).catch(res => {
                    if (res.response.status === 422) {
                        const errors = Object.values(res.response.data.errors).flat(Infinity);
                        this.messageDialogText = [];
                        errors.forEach(err => {
                            this.messageDialogText.push(err);
                        })
                        this.messageDialogTitle = 'Error';
                        this.$refs.messageDialogModal.open();
                    }
                }).finally(() => {
                    this.loading = false;
                });
            }
        },
    },
    beforeDestroy() {
        this.$eventBus.$emit('clearComponentWillHandleErrorCodes', this._uid);
    }
}
</script>

<style scoped>

</style>
